import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import api from "@/lib/api";

// Use plain DivIcons (no external image dependency on Leaflet's default icons)
function makeIcon(kind, vacant) {
  const symbols = {
    mfh: "🏢",
    efh: "🏠",
    dhh: "🏠",
    rh: "🏘️",
    commercial: "🏬",
    garage: "🅿️",
    plot: "📍",
  };
  const sym = symbols[(kind || "mfh").toLowerCase()] || "🏢";
  const bg = vacant > 0 ? "#b45309" : "#0F172A";
  const html = `
    <div style="
      width:34px;height:42px;
      display:flex;flex-direction:column;align-items:center;justify-content:flex-start;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));
    ">
      <div style="
        width:30px;height:30px;border-radius:50%;
        background:${bg}; color:#fff;
        display:flex;align-items:center;justify-content:center;
        font-size:16px; line-height:1; border:2px solid #fff;
      ">${sym}</div>
      <div style="
        width:0;height:0;
        border-left:5px solid transparent;border-right:5px solid transparent;
        border-top:8px solid ${bg};margin-top:-2px;
      "></div>
    </div>`;
  return L.divIcon({
    className: "domivus-marker",
    html,
    iconSize: [34, 42],
    iconAnchor: [17, 42],
    popupAnchor: [0, -36],
  });
}

const PROP_KIND_LABEL = {
  mfh: "Mehrfamilienhaus", efh: "Einfamilienhaus", dhh: "Doppelhaushälfte",
  rh: "Reihenhaus", commercial: "Gewerbe", garage: "Garage/Stellplatz", plot: "Grundstück",
};

export default function PropertyMap() {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/properties/map/markers").then(({ data }) => {
      setMarkers(data || []);
    }).finally(() => setLoading(false));
  }, []);

  // Auto-fit center: Germany if no markers
  const center = markers.length ? [markers[0].lat, markers[0].lng] : [51.1657, 10.4515];
  const zoom = markers.length ? 6 : 6;

  return (
    <div className="space-y-2" data-testid="property-map">
      <div className="flex items-center justify-between text-xs text-slate-600">
        <div><strong>{markers.length}</strong> geokodierte Objekte · Karte: OpenStreetMap</div>
        {loading && <div className="text-slate-400">Lade…</div>}
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white shadow-sm" style={{ height: 540 }}>
        <MapContainer center={center} zoom={zoom} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <FitBounds markers={markers} />
          {markers.map((m) => (
            <Marker key={m.id} position={[m.lat, m.lng]} icon={makeIcon(m.kind, m.vacant)}>
              <Popup>
                <div className="text-sm" style={{ minWidth: 220 }}>
                  <div className="font-semibold text-slate-900">{m.property_number} · {m.name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {m.address?.street} {m.address?.house_number}, {m.address?.zip} {m.address?.city}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Typ: <strong>{PROP_KIND_LABEL[(m.kind || "").toLowerCase()] || m.kind}</strong>
                  </div>
                  <div className="text-xs mt-1">
                    Einheiten: <strong>{m.units}</strong>{" "}
                    {m.vacant > 0 ? <span className="text-amber-700">· {m.vacant} leer</span> : <span className="text-emerald-700">· voll vermietet</span>}
                  </div>
                  <Link to={`/objekte/${m.id}`} className="text-emerald-700 underline text-xs mt-2 inline-block">Objekt öffnen →</Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

function FitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [30, 30] });
    // eslint-disable-next-line
  }, [markers]);
  return null;
}
